const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = {
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  SPECIAL: /[!@#$%^&*(),.?":{}|<>]/,
};
const NUMERIC_REGEX = /^\d+$/;
const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
const PEOPLE_MAX = 20;
const PREBOOKING_THRESHOLD = 10;
const TABLE_NAME_MAX_LENGTH = 50;
const STAFF_NAME_MAX_LENGTH = 60;

const ERROR_MESSAGES = {
  EMAIL_REQUIRED: "The email address is required.",
  EMAIL_NO_AT: "Email must contain an '@' symbol.",
  EMAIL_TOO_SHORT: "Email address is too short.",
  EMAIL_INVALID_FORMAT: "Email address format is invalid.",
  PASSWORD_REQUIRED: "The Password field is required.",
  PASSWORD_LENGTH: "Password must be between 8 and 12 characters.",
  PASSWORD_CASE: "Password must contain both uppercase and lowercase letters.",
  PASSWORD_SPECIAL: "Password must contain at least one special character.",
  PEOPLE_REQUIRED: "Number of People is required",
  PEOPLE_INVALID: "Invalid number of people. Must be a positive integer.",
  PEOPLE_LARGE_GROUP: `Pre Booking required for groups of ${PREBOOKING_THRESHOLD} or more.`,
  PEOPLE_MAX: `Maximum ${PEOPLE_MAX} people per table. Please contact for larger groups.`,
};

export function validateEmail(email: string | null | undefined) {
  if (!email) return ERROR_MESSAGES.EMAIL_REQUIRED;
  if (!email.includes("@")) return ERROR_MESSAGES.EMAIL_NO_AT;
  if (email.length < 5) return ERROR_MESSAGES.EMAIL_TOO_SHORT;
  if (!EMAIL_REGEX.test(email)) return ERROR_MESSAGES.EMAIL_INVALID_FORMAT;
  return null;
}

export function validatePassword(password: string | null | undefined) {
  if (!password) return ERROR_MESSAGES.PASSWORD_REQUIRED;

  if (password.length < 8 || password.length > 12) {
    return ERROR_MESSAGES.PASSWORD_LENGTH;
  }

  if (!PASSWORD_REGEX.UPPERCASE.test(password) || !PASSWORD_REGEX.LOWERCASE.test(password)) {
    return ERROR_MESSAGES.PASSWORD_CASE;
  }

  if (!PASSWORD_REGEX.SPECIAL.test(password)) {
    return ERROR_MESSAGES.PASSWORD_SPECIAL;
  }

  return null;
}

export function validateNumberOfPeople(value: string | number) {
  if (value === null || value === undefined || value === "") {
    return ERROR_MESSAGES.PEOPLE_REQUIRED;
  }

  const stringValue = String(value).trim();

  if (!NUMERIC_REGEX.test(stringValue)) {
    return ERROR_MESSAGES.PEOPLE_INVALID;
  }

  const num = parseInt(stringValue, 10);
  if (num <= 0) {
    return ERROR_MESSAGES.PEOPLE_INVALID;
  }

  if (num >= PREBOOKING_THRESHOLD) {
    return ERROR_MESSAGES.PEOPLE_LARGE_GROUP;
  }

  if (num > PEOPLE_MAX) {
    return ERROR_MESSAGES.PEOPLE_MAX;
  }

  return null;
}

export function validatePhoneNumber(phone: string | null | undefined) {
  if (!phone || !phone.trim()) {
    return "Phone number is required";
  }

  const normalized = phone.trim();
  const digitsOnly = normalized.replace(/\D/g, "");

  if (!digitsOnly) {
    return "Phone number is required";
  }

  if (!/^[+\d\s\-()]+$/.test(normalized)) {
    return "Please enter a valid WhatsApp phone number";
  }

  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return "Phone number must be between 10 and 15 digits";
  }

  if (normalized.includes("+") && !/^\+\d[\d\s\-()]*$/.test(normalized)) {
    return "Use '+' only at the start of the phone number";
  }

  return null;
}

export function validateRequired(value: string | null | undefined, fieldName = "Field") {
  if (!value || !value.trim()) {
    return `${fieldName} is required`;
  }
  return null;
}

export function validateLength(
  value: string | null | undefined,
  minLength: number,
  maxLength: number,
  fieldName = "Field"
) {
  if (!value) return `${fieldName} is required`;

  if (value.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters long`;
  }

  if (value.length > maxLength) {
    return `${fieldName} must be no more than ${maxLength} characters long`;
  }

  return null;
}

export function validateStaffName(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";

  if (!trimmed) {
    return "Full name is required";
  }

  if (trimmed.length < 2) {
    return "Full name must be at least 2 characters long";
  }

  if (trimmed.length > STAFF_NAME_MAX_LENGTH) {
    return `Full name must be no more than ${STAFF_NAME_MAX_LENGTH} characters long`;
  }

  return null;
}

export function validateTableName(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";

  if (!trimmed) {
    return "Table name is required";
  }

  if (trimmed.length < 2) {
    return "Table name must be at least 2 characters long";
  }

  if (trimmed.length > TABLE_NAME_MAX_LENGTH) {
    return `Table name must be no more than ${TABLE_NAME_MAX_LENGTH} characters long`;
  }

  return null;
}

export function validateTableCapacity(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "Capacity is required";
  }

  const stringValue = String(value).trim();
  if (!NUMERIC_REGEX.test(stringValue)) {
    return "Capacity must be a whole number";
  }

  const capacity = parseInt(stringValue, 10);
  if (capacity < 1) {
    return "Capacity must be at least 1";
  }

  if (capacity > PEOPLE_MAX) {
    return `Capacity cannot exceed ${PEOPLE_MAX}`;
  }

  return null;
}

export function validateReservationTime(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";

  if (!trimmed) {
    return "Reservation time is required";
  }

  if (!TIME_24H_REGEX.test(trimmed)) {
    return "Reservation time must use HH:MM (24-hour) format";
  }

  return null;
}
